import { useToast } from 'vue-toastification'
import i18n from '../../locales/i18n'
import {
  GetTasks,
  GetTask,
  GetTimesByTaskId,
  GetMaterialsByTaskId
} from '@/api/task.js'
import {
  PostMessage,
  GetMessagesByTaskId,
  SetTaskAsRead
} from '@/api/message.js'

const toast = useToast()

const sortOptions = {
  user: { key: 'Sender', reverse: false },
  message: { key: 'Message', reverse: false },
  timestamp: { key: 'Created', reverse: false },
  document: { key: 'Name', reverse: false }
}

const state = () => ({
  tasks: [],
  activeTasks: [],
  finishedTasks: [],
  plannedServiceVisits: [],
  finishedServiceVisits: [],
  totalCount: 0,
  currentTask: undefined,
  currentTaskTimes: [],
  currentTaskMaterials: [],
  taskMesssage: '',
  currentSort: sortOptions.timestamp,
  currentDocumentSort: sortOptions.timestamp
})

const getters = {
}
// actions
const actions = {
  async fetchTasksWithFilter ({ commit }, filter) {
    console.log(filter)
    try {
      const taskStatusesArray = Array.from(filter.taskStatuses || [])
      const params = new URLSearchParams()

      // Append pagination and sorting
      params.append('filter.page', filter.page ?? 1)
      params.append('filter.pageSize', filter.pageSize || 10)
      params.append('filter.orderBy', filter.orderBy || 'PlannedDate')
      params.append('filter.descending', filter.descending || false) // "true"/"false" as string or boolean

      // Append task statuses
      taskStatusesArray.forEach(status => {
        params.append('filter.taskStatuses', status)
      })

      if (filter.includeOfferingType) {
        const includeTypes = Array.isArray(filter.includeOfferingType) ? filter.includeOfferingType : [filter.includeOfferingType]
        includeTypes.forEach(type => {
          params.append('filter.UPFilters', `10073:EQ:${type}`)
        })
      }

      // Append exclude offering types
      if (filter.excludeOfferingTypes) {
        const excludeTypes = Array.from(filter.excludeOfferingTypes)
        excludeTypes.forEach(type => {
          params.append('filter.UPFilters', `10073:NEQ:${type}`)
        })
      }
      console.log(`?${params.toString()}`)
      const response = await GetTasks(`?${params.toString()}`)

      if (response.status === 200) {
        return response.data.Tasks
      } else {
        toast.warning(i18n.global.t('taskPage.couldNotGetData'))
        return []
      }
    } catch (e) {
      toast.warning(i18n.global.t('taskPage.couldNotGetData'))
      return []
    }
  },

  async fetchActiveTasks ({ commit, dispatch }) {
    const filter = {
      taskStatuses: [0, 1, 2, 3],
      excludeOfferingTypes: ['22 - Service contract', '23 - Energy and climate contract'],
      page: 0,
      pageSize: 20,
      orderBy: 'PlannedDate',
      descending: false
    }
    const tasks = await dispatch('fetchTasksWithFilter', filter)
    commit('setActiveTasks', tasks)
  },

  async fetchFinishedTasks ({ commit, dispatch }) {
    const filter = {
      taskStatuses: [4, 5],
      excludeOfferingTypes: ['22 - Service contract', '23 - Energy and climate contract'],
      page: 0,
      pageSize: 20,
      orderBy: 'PlannedDate',
      descending: false
    }
    const tasks = await dispatch('fetchTasksWithFilter', filter)
    commit('setFinishedTasks', tasks)
  },

  async fetchPlannedServiceVisits ({ commit, dispatch }) {
    const filter = {
      taskStatuses: [0, 1, 2, 3],
      includeOfferingType: ['22 - Service contract'],
      page: 0,
      pageSize: 20,
      orderBy: 'PlannedDate',
      descending: false
    }
    const tasks = await dispatch('fetchTasksWithFilter', filter)
    commit('setPlannedServiceVisits', tasks)
  },

  // Example action to fetch finished service visits (status >= 4, including offering type '22 - Service contract')
  async fetchFinishedServiceVisits ({ commit, dispatch }) {
    const filter = {
      taskStatuses: [4, 5], // Example task statuses (finished ones)
      includeOfferingType: '22 - Service contract',
      page: 0,
      pageSize: 20,
      orderBy: 'PlannedDate',
      descending: false
    }
    const tasks = await dispatch('fetchTasksWithFilter', filter)
    commit('setFinishedServiceVisits', tasks)
  },
  async getBaseData ({ commit }, {
    page = 0,
    pageSize = 10,
    taskStatuses = [],
    orderBy = 'PlannedDate',
    descending = false
  } = {}) {
    try {
      const taskStatusesArray = Array.from(taskStatuses)
      const params = new URLSearchParams()

      params.append('filter.page', page)
      params.append('filter.pageSize', pageSize)
      params.append('filter.orderBy', orderBy)
      params.append('filter.descending', descending) // boolean or "true"/"false" string — depends on backend

      taskStatusesArray.forEach(status => {
        params.append('filter.taskStatuses', status)
      })

      // console.log('final filter:', params.toString())

      const taskResponse = await GetTasks(`?${params.toString()}`)
      // console.log(taskResponse.data.Tasks)
      if (taskResponse.status === 200) {
        const taskdata = taskResponse.data.Tasks
        for (const task of taskdata) {
          task.hidden = false
        }
        console.log(taskdata)
        const totalCount = taskResponse.data.Count || 0
        commit('setTasks', taskdata)
        commit('setTotalCount', totalCount)
      } else {
        toast.warning(i18n.global.t('taskPage.couldNotGetData'))
      }
    } catch (e) {
      // console.log(e)
      toast.warning(i18n.global.t('taskPage.couldNotGetData'))
    }
  },
  async getTaskById ({ commit, dispatch }, id) {
    commit('setCurrentTask', undefined)
    try {
      const taskResponse = await GetTask(id)
      // console.log(taskResponse)
      if (taskResponse.status === 200) {
        const taskdata = taskResponse.data

        commit('setCurrentTask', taskdata)

        const taskMaterialsResponse = await GetMaterialsByTaskId(id)
        if (taskMaterialsResponse.status === 200) {
          const taskMaterials = taskMaterialsResponse.data
          commit('setCurrentTaskMaterials', taskMaterials.Materials)
        } else {
          toast.warning(i18n.global.t('taskPage.couldNotGetData'))
        }
        const taskTimesResponse = await GetTimesByTaskId(id)
        if (taskTimesResponse.status === 200) {
          const taskTimes = taskTimesResponse.data
          commit('setCurrentTaskTimes', taskTimes.Times)
        } else {
          toast.warning(i18n.global.t('taskPage.couldNotGetData'))
        }
        const taskMessagesResponse = await GetMessagesByTaskId(id)
        if (taskMessagesResponse.status === 200) {
          const taskMessages = taskMessagesResponse.data
          commit('setCurrentTaskMessages', taskMessages.Messages)
          if (taskMessages.Unread && taskMessages.Unread > 0) {
            await SetTaskAsRead(id)
            // dispatch('message/getBaseData')
            // dispatch('message/updateUnreadTaskCount', -1)
          }
        } else {
          toast.warning(i18n.global.t('taskPage.couldNotGetData'))
        }
      } else {
        toast.warning(i18n.global.t('taskPage.couldNotGetData'))
      }
    } catch (e) {
      // console.log(e)
      toast.warning(i18n.global.t('taskPage.couldNotGetData') + ' ' + e.response.data.Message)
    }
  },
  async postMessage ({ state, commit, dispatch }, id) {
    if (!state.taskMessage) {
      return
    }
    const json = {
      CustomerId: state.currentTask.Customer.Id,
      TaskId: state.currentTask.Id,
      Subject: state.taskMessage,
      Message: state.taskMessage
    }
    try {
      const result = await PostMessage(json)
      if (result.status === 200) {
        commit('addMessageToCurrentTask', result.data)
        commit('setTaskMessage', '')
        // dispatch('getTaskById', state.currentTask.Id)
      } else {
        toast.warning(i18n.global.t('taskPage.couldNotPostMessage'))
      }
    } catch (e) {
      toast.warning(i18n.global.t('taskPage.couldNotPostMessage'))
    }
  },
  setSort ({ commit, state }, option) {
    // If a different column is clicked, set it as the current sort column
    if (state.currentSort.key !== option.key) {
      commit('setCurrentSort', { key: option.key, reverse: false })
    } else {
      // If the same column is clicked again, toggle the order
      commit('setCurrentSort', { key: option.key, reverse: !state.currentSort.reverse })
    }

    // Apply sorting to the Messages array
    const sortedMessages = [...state.currentTask.Messages].sort((a, b) => {
      const aValue = a[state.currentSort.key]
      const bValue = b[state.currentSort.key]

      // Adjust the comparison based on the data type of the column
      if (typeof aValue === 'string') {
        return state.currentSort.reverse ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue)
      } else {
        return state.currentSort.reverse ? bValue - aValue : aValue - bValue
      }
    })

    // Commit the mutation to update the store state
    commit('setSortedMessages', sortedMessages)
  },
  setSortDocument ({ commit, state }, option) {
    // If a different column is clicked, set it as the current sort column
    if (state.currentDocumentSort.key !== option.key) {
      commit('setDocumentSort', { key: option.key, reverse: false })
    } else {
      // If the same column is clicked again, toggle the order
      commit('setDocumentSort', { key: option.key, reverse: !state.currentDocumentSort.reverse })
    }
  }
}

const mutations = {
  setActiveTasks (state, data) {
    state.activeTasks = data
  },
  setFinishedTasks (state, data) {
    state.finishedTasks = data
  },
  setPlannedServiceVisits (state, data) {
    state.plannedServiceVisits = data
  },
  setFinishedServiceVisits (state, data) {
    state.finishedServiceVisits = data
  },
  setTasks (state, data) {
    state.tasks = data
  },
  setTotalCount (state, count) {
    state.totalCount = count
  },
  setCurrentTask (state, data) {
    state.currentTask = data
  },
  setCurrentTaskMaterials (state, data) {
    state.currentTaskMaterials = data
  },
  setCurrentTaskTimes (state, data) {
    state.currentTaskTimes = data
  },
  setTaskMessage (state, data) {
    state.taskMessage = data
  },
  setCurrentTaskMessages (state, data) {
    state.currentTask.Messages = data
  },
  setHidden (state, { taskId, hidden }) {
    const taskIndex = state.tasks.findIndex(task => task.Id === taskId)
    if (taskIndex !== -1) {
      state.tasks[taskIndex].Hidden = hidden
    }
  },
  addMessageToCurrentTask (state, newMessage) {
    const insertIndex = state.currentTask.Messages.findIndex((message) => {
      const sortKey = state.currentSort.key
      const newValue = newMessage[sortKey]
      const existingValue = message[sortKey]

      if (typeof newValue === 'string') {
        return state.currentSort.reverse ? newValue.localeCompare(existingValue) > 0 : newValue.localeCompare(existingValue) < 0
      } else {
        return state.currentSort.reverse ? newValue - existingValue > 0 : newValue - existingValue < 0
      }
    })

    if (insertIndex !== -1) {
      state.currentTask.Messages.splice(insertIndex, 0, newMessage)
    } else {
      state.currentTask.Messages.push(newMessage)
    }
  },
  setSortedMessages (state, sortedMessages) {
    state.currentTask.Messages = sortedMessages
  },
  setCurrentSort (state, option) {
    state.currentSort = option
  },
  setDocumentSort (state, option) {
    state.currentDocumentSort = option
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
